import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ApolloError } from '@apollo/client';
import { Navigate } from 'react-router-dom';

import { ScreenLoader } from '@ocx/ui';
import { useGetNavigationActions } from '@ocx-app/hooks/useGetNavigationActions';
import { useRudderStack } from '@ocx-app/lib/rudderStack/useRudderStack';
import { PrimaryInternalFuelingStatus } from '@ocx-app/modules/transaction/types';
import { useInfoActionSheet } from '@ocx/feat-modals';
import { AppRoutePath } from '@ocx-app/pages/AppRoutePath';

import { FuelingStatus } from './fueling-status.component';
import { FuelingStatusLayout } from './fueling-status.layout';
import { useFuelingStatusController } from './hooks/use-fueling-status-controller.hook';
import { statusToMessageMap } from './fueling-status.mapping';
import { fuelingFailedMessage, transactionStatusTimedOutMessage } from './fueling-status.messages';

type FuelingState = {
  status: PrimaryInternalFuelingStatus;
  label: string;
  secondaryLabel: string | null;
  canLeave: boolean;
};

export const FuelingStatusController = () => {
  const intl = useIntl();
  const { triggerEvent } = useRudderStack();

  const { open: openInfoActionSheet } = useInfoActionSheet();
  const handleTransactionError = useCallback(
    (error?: ApolloError) =>
      openInfoActionSheet({
        title: intl.formatMessage(fuelingFailedMessage),
        description: error?.message,
        isServiceLinkEnabled: true,
      }),
    [intl, openInfoActionSheet],
  );
  const { status, loading, isTransactionStatusTimedOut, transactionId, pumpNumber } = useFuelingStatusController({
    onError: handleTransactionError,
  });
  const { replaceToReceipt, pushToHome } = useGetNavigationActions();
  const navigateToReceipt = useCallback(() => {
    triggerEvent('pay_at_pump_view_receipt');
    replaceToReceipt(transactionId);
  }, [replaceToReceipt, transactionId, triggerEvent]);
  const navigateToHome = useCallback(() => pushToHome(), [pushToHome]);

  const state = useMemo<FuelingState | null>(() => {
    if (!status) {
      return null;
    }

    const { primary, secondary } = statusToMessageMap[status];

    const label = intl.formatMessage(
      primary,
      status === PrimaryInternalFuelingStatus.Authorizing ? { pumpNumber } : undefined,
    );

    if (isTransactionStatusTimedOut) {
      return {
        status: PrimaryInternalFuelingStatus.Unknown,
        label,
        secondaryLabel: intl.formatMessage(transactionStatusTimedOutMessage),
        canLeave: true,
      };
    }

    return {
      status,
      label,
      secondaryLabel: secondary ? intl.formatMessage(secondary) : null,
      canLeave: [
        PrimaryInternalFuelingStatus.CompletedSuccessfully,
        PrimaryInternalFuelingStatus.CompletedUnsuccessfully,
      ].includes(status),
    };
  }, [intl, isTransactionStatusTimedOut, pumpNumber, status]);

  if (!state && loading) {
    return (
      <FuelingStatusLayout>
        <ScreenLoader />
      </FuelingStatusLayout>
    );
  }

  if (!state) {
    return <Navigate to={AppRoutePath.Home} replace />;
  }

  return (
    <FuelingStatusLayout>
      <FuelingStatus
        status={state.status}
        label={state.label}
        secondaryLabel={state.secondaryLabel}
        navigateToReceipt={navigateToReceipt}
        navigateToHome={navigateToHome}
      />
    </FuelingStatusLayout>
  );
};
